/**
 * LocalPListPage — Purchase Local P contract list.
 *
 * Access 1:1 parity per DocConvert_PurchaseLocalP.md:
 *   - 70-column datasheet matching frmPurchaseQryLocalP / public_mv_local_p
 *   - Toolbar: New, Edit, Cancel, Copy, Print-Cancel, Print-P Contract, Delete
 *   - Status based on cancel_adjust (not is_status)
 *   - Access-parity blocker messages (English)
 */
import { type ComponentProps, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { usePermission } from '@/hooks/usePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCancelLocalPMutation,
  useDeleteLocalPMutation,
  useGetLocalPCanHardDeleteQuery,
  useGetLocalPQuery,
  useLazyGetLocalPQuery,
  useLazyGetLocalPPrintChooseQuery,
  useLazyPrintLocalPQuery,
  useListLocalPQuery,
} from '@/store/api/localPApi'
import type {
  LocalPListItem,
  LocalPPrintChooseData,
  LocalPPrintKind,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { printAndUploadReport } from '@/utils/printAndUploadReport'

import LocalPFormDialog from './LocalPFormDialog'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search' | 'status'

/** TablePagination «All» maps to this pageSize (single request; real row count <= total). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

// Readonly snapshot field — bg phân biệt với field nhập được (chuẩn skill
// /convert-form, đồng bộ các form CRUD).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type LocalPPrintDialogState = {
  row: LocalPListItem
  kind: Extract<LocalPPrintKind, 'cancel' | 'contract'>
}

type LocalPPrintChooseForm = {
  contract_no: string
  thanhtoan_choose: string
  thanhtoan: string
  thongtinnganhang: string
  fee_txt_pr: string
  quality_code: string
  quality_des_contract: string
}

type MuiTableProps = ComponentProps<typeof Table>
type MuiTableHeadProps = ComponentProps<typeof TableHead>
type MuiTableBodyProps = ComponentProps<typeof TableBody>
type MuiTableRowProps = ComponentProps<typeof TableRow>
type VirtuosoTableRowProps = MuiTableRowProps & { 'data-item-index'?: number | string }

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

function buildLocalPPrintPreviewHtml(rawHtml: string): string {
  if (!rawHtml) return ''
  if (rawHtml.includes('<base ')) return rawHtml
  return rawHtml.replace('<head>', `<head><base href="${window.location.origin}/">`)
}

function toLocalPPrintChooseForm(data: LocalPPrintChooseData): LocalPPrintChooseForm {
  return {
    contract_no: data.contract_no,
    thanhtoan_choose: '',
    thanhtoan: data.thanhtoan ?? '',
    thongtinnganhang: data.thongtinnganhang ?? '',
    fee_txt_pr: data.fee_txt_pr ?? '',
    quality_code: data.quality_code ?? '',
    quality_des_contract: data.quality_des_contract ?? '',
  }
}

interface LocalPPrintDialogProps {
  open: boolean
  printTarget: LocalPPrintDialogState | null
  onClose: () => void
}

type LocalPNoticeDialogState = {
  title: string
  message: string
}

function LocalPPrintDialog({ open, printTarget, onClose }: LocalPPrintDialogProps) {
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')
  const [chooseForm, setChooseForm] = useState<LocalPPrintChooseForm | null>(null)
  const [paymentChoices, setPaymentChoices] = useState<LocalPPrintChooseData['payment_choices']>([])
  const [previewMeta, setPreviewMeta] = useState<{
    contractNo: string
    title: string
    iframeTitle: string
    suffix: string
  } | null>(null)
  const [toastMessage, setToastMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [fetchPrintChoose, { isFetching: isLoadingChoose }] = useLazyGetLocalPPrintChooseQuery()
  const [triggerPrint, { isFetching }] = useLazyPrintLocalPQuery()
  const autoPreviewKeyRef = useRef('')

  const contractNo = printTarget?.row.contract_no ?? ''
  const isChooseFormReady = chooseForm?.contract_no === contractNo

  useEffect(() => {
    if (!open || printTarget?.kind !== 'contract' || !contractNo) {
      return
    }
    let isActive = true
    void fetchPrintChoose(contractNo)
      .unwrap()
      .then((result) => {
        if (!isActive) return
        setChooseForm(toLocalPPrintChooseForm(result.data))
        setPaymentChoices(result.data.payment_choices)
      })
      .catch((err) => {
        if (!isActive) return
        setErrorMessage(getErrorMessage(err, 'Failed to load print options'))
      })
    return () => {
      isActive = false
    }
  }, [contractNo, fetchPrintChoose, open, printTarget?.kind])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleClosePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl('')
    setPreviewHtml('')
    setPreviewMeta(null)
  }, [previewUrl])

  const handleCloseDialog = useCallback(() => {
    handleClosePreview()
    onClose()
  }, [handleClosePreview, onClose])

  const handleSaveAndPreview = useCallback(async () => {
    if (!printTarget) return
    if (printTarget.kind === 'contract' && (!chooseForm || !isChooseFormReady)) {
      setErrorMessage('Print options are still loading.')
      return
    }
    try {
      const result = await triggerPrint({
        contractNo: printTarget.row.contract_no,
        kind: printTarget.kind,
        choose: printTarget.kind === 'contract' && chooseForm
          ? {
              thanhtoan: chooseForm.thanhtoan,
              thongtinnganhang: chooseForm.thongtinnganhang,
              fee_txt_pr: chooseForm.fee_txt_pr,
              quality_des_contract: chooseForm.quality_des_contract,
            }
          : undefined,
      }).unwrap()
      const isCancel = printTarget.kind === 'cancel'
      const isForwardContract = result.data.filename.includes('contract-forward')
      if (result.data.html) {
        handleClosePreview()
        setPreviewMeta({
          contractNo: printTarget.row.contract_no,
          title: isCancel
            ? 'Print Preview - Cancel'
            : isForwardContract
              ? 'Print Preview - P Contract (Forward)'
              : 'Print Preview - P Contract',
          iframeTitle: isCancel ? 'Print Cancel Preview' : 'Purchase Contract Preview',
          // Filename suffix theo tab "Purchase" + variant (Forward/Cancel nếu có).
          suffix: isCancel ? 'PU_Contract_Cancel' : isForwardContract ? 'PU_Contract_Forward' : 'PU_Contract',
        })
        setPreviewHtml(buildLocalPPrintPreviewHtml(result.data.html))
        setToastMessage(result.message ?? 'Print preview generated.')
        onClose()
        return
      }
      if (!result.data.pdf_base64) {
        setErrorMessage('Print preview could not open because the server did not return a PDF.')
        return
      }
      handleClosePreview()
      setPreviewMeta({
        contractNo: printTarget.row.contract_no,
        title: isCancel ? 'Print Preview - Cancel' : 'Print Preview - P Contract',
        iframeTitle: isCancel ? 'Print Cancel Preview' : 'Purchase Contract Preview',
        suffix: isCancel ? 'PU_Contract_Cancel' : 'PU_Contract',
      })
      if (isCancel && result.data.html) {
        setPreviewHtml(buildLocalPPrintPreviewHtml(result.data.html))
      } else {
        const pdfBytes = Uint8Array.from(atob(result.data.pdf_base64), (c) => c.charCodeAt(0))
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        setPreviewUrl(URL.createObjectURL(blob))
      }
      setToastMessage(result.message ?? 'Print preview generated.')
      onClose()
    } catch (err) {
      setErrorMessage(getErrorMessage(err, printTarget.kind === 'cancel' ? 'Print Cancel failed' : 'Print P Contract failed'))
    }
  }, [chooseForm, handleClosePreview, isChooseFormReady, onClose, printTarget, triggerPrint])

  useEffect(() => {
    if (!open) {
      autoPreviewKeyRef.current = ''
      return
    }
    if (printTarget?.kind !== 'cancel') return
    const key = `${printTarget.row.contract_no}:${printTarget.kind}`
    if (autoPreviewKeyRef.current === key) return
    autoPreviewKeyRef.current = key
    const timer = window.setTimeout(() => {
      void handleSaveAndPreview()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [handleSaveAndPreview, open, printTarget])

  return (
    <>
      <Dialog open={open && printTarget?.kind !== 'cancel'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{printTarget?.kind === 'cancel' ? 'Print Cancel' : 'Choose Value'}</DialogTitle>
        <DialogContent dividers>
          {isLoadingChoose || !isChooseFormReady ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontStyle: 'italic' }}>
                Input Value for Print Form
              </Typography>
              {/* Access evidence: form_frmPurchaseQryLocalPPr_Choose.txt field order. */}
              <TextField
                label="Contract P"
                size="small"
                value={chooseForm?.contract_no ?? contractNo}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={readonlyFieldSx}
              />
              <TextField
                select
                label="Payment"
                size="small"
                value={chooseForm?.thanhtoan_choose ?? ''}
                onChange={(e) => {
                  const code = e.target.value
                  const choice = paymentChoices.find((item) => item.code === code)
                  setChooseForm((current) =>
                    current
                      ? {
                          ...current,
                          thanhtoan_choose: code,
                          thanhtoan: choice?.description ?? '',
                        }
                      : current,
                  )
                }}
                disabled={!chooseForm}
              >
                <MenuItem value="">
                  <em />
                </MenuItem>
                {paymentChoices.map((choice) => (
                  <MenuItem key={choice.code} value={choice.code}>
                    {choice.code}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Payment Description"
                size="small"
                value={chooseForm?.thanhtoan ?? ''}
                multiline
                minRows={3}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={readonlyFieldSx}
              />
              <TextField
                label="Quality"
                size="small"
                value={chooseForm?.quality_code ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={readonlyFieldSx}
              />
              <TextField
                label="Quality Description"
                size="small"
                value={chooseForm?.quality_des_contract ?? ''}
                multiline
                minRows={3}
                onChange={(e) =>
                  setChooseForm((current) =>
                    current ? { ...current, quality_des_contract: e.target.value } : current,
                  )
                }
                disabled={!chooseForm}
              />
              <TextField
                label="Other information"
                size="small"
                value={chooseForm?.thongtinnganhang ?? ''}
                multiline
                minRows={3}
                onChange={(e) =>
                  setChooseForm((current) =>
                    current ? { ...current, thongtinnganhang: e.target.value } : current,
                  )
                }
                disabled={!chooseForm}
              />
              <TextField
                label="Cert Fee Text"
                size="small"
                value={chooseForm?.fee_txt_pr ?? ''}
                multiline
                minRows={3}
                onChange={(e) =>
                  setChooseForm((current) =>
                    current ? { ...current, fee_txt_pr: e.target.value } : current,
                  )
                }
                disabled={!chooseForm}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveAndPreview}
              disabled={!printTarget || isFetching || isLoadingChoose || !isChooseFormReady}
            >
              Save
            </Button>
            {isFetching && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(previewHtml || previewUrl)} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span">{previewMeta?.title ?? 'Print Preview'}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(previewMeta?.contractNo || 'purchase-contract')}_${previewMeta?.suffix ?? 'Print'}.pdf`
                const reportName = previewMeta?.suffix === 'PU_Contract_Cancel'
                  ? 'rpPurchaseCancel'
                  : previewMeta?.suffix === 'PU_Contract_Forward'
                    ? 'rpPurchaseContractP_forward'
                    : 'rpPurchaseContractP'
                void printAndUploadReport({
                  iframeTitle: previewMeta?.iframeTitle ?? 'Print Cancel Preview',
                  reportName,
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          {previewHtml ? (
            <Box
              component="iframe"
              title={previewMeta?.iframeTitle ?? 'Print Cancel Preview'}
              srcDoc={previewHtml}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
            />
          ) : (
            <Box
              component="iframe"
              title={previewMeta?.iframeTitle ?? 'Purchase Print Preview'}
              src={previewUrl}
              onError={() => setErrorMessage('Print preview could not open. Please try again.')}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')} maxWidth="xs" fullWidth>
        <DialogTitle>Print Error</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setErrorMessage('')}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}

interface LocalPNoticeDialogProps {
  open: boolean
  notice: LocalPNoticeDialogState | null
  onClose: () => void
}

function LocalPNoticeDialog({ open, notice, onClose }: LocalPNoticeDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{notice?.title ?? 'Message'}</DialogTitle>
      <DialogContent>
        <DialogContentText>{notice?.message ?? ''}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'percent'
type ColumnDef = {
  key: keyof LocalPListItem | string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** All Access visible columns — exact order and captions from frmPurchaseQryLocalP (DocConvert §4.2). */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no',         label: 'Contract No' },
  { key: 'contract_date',       label: 'Contract Date', kind: 'date' },
  { key: 'supplier_code',       label: 'Supplier Code', filter: 'checkboxes' },
  { key: 'npe_ref',             label: 'NPEs Ref' },
  { key: 'seller_ref',          label: "Seller's Ref" },
  { key: 'supplier_name',       label: 'Supplier Name', filter: 'checkboxes' },
  { key: 'quantity_kg',         label: 'Kg', kind: 'number', align: 'right' },
  { key: 'no_of_bag',           label: 'Bags', kind: 'number', align: 'right' },
  { key: 'quality_code',        label: 'Quality Code', filter: 'checkboxes' },
  { key: 'certification',       label: 'Certification', filter: 'checkboxes' },
  { key: 'me_or_ib',            label: 'ME or IB' },
  { key: 'license_cert_no',     label: 'License Cert No.' },
  { key: 'si',                  label: 'SI (VND/KG)', kind: 'number', align: 'right' },
  { key: 'sd',                  label: 'SD (VND/KG)', kind: 'number', align: 'right' },
  { key: 'package_code',        label: 'Package Code' },
  { key: 'packaging',           label: 'Package Name' },
  { key: 'contract_type',       label: 'Contract Type', filter: 'checkboxes' },
  { key: 'outright_price',      label: 'Outright Price', kind: 'number', align: 'right' },
  { key: 'ptbf_diffs',          label: 'PTBF Diffs', kind: 'number', align: 'right' },
  { key: 'price_unit',          label: 'Price Unit' },
  { key: 'vat_txt',             label: 'Appliable VAT' },
  { key: 'futures_month',       label: 'Futures Month', filter: 'checkboxes' },
  { key: 'fixation_call',       label: 'Fixation Call' },
  { key: 'latest_delivery',     label: 'Latest Delivery', kind: 'date' },
  { key: 'delivery_term',       label: 'Delivery Term' },
  { key: 'delivery_place_code', label: 'Delivery Place Code' },
  { key: 'delivery_place',      label: 'Delivery Place' },
  { key: 'note',                label: 'Notes' },
  { key: 'received',            label: 'Received', kind: 'number', align: 'right' },
  { key: 'pending',             label: 'Pending', kind: 'number', align: 'right' },
  { key: 'fx',                  label: 'FX', kind: 'number', align: 'right' },
  { key: 'logistic_cost',       label: 'Logistics costs', kind: 'number', align: 'right' },
  { key: 'premium_vs_g2_5percent', label: 'Quality Premium', kind: 'number', align: 'right' },
  { key: 'cert_premium',        label: 'Cert premium', kind: 'number', align: 'right' },
  { key: 'package_premium',     label: 'Package premium', kind: 'number', align: 'right' },
  { key: 'hedged_price',        label: 'Hedged Price', kind: 'number', align: 'right' },
  { key: 'implied_diffs',       label: 'Implied Diffs', kind: 'number', align: 'right' },
  { key: 'diffs_g2_fob_bulk',   label: 'G2 FOB Diff Bulk', kind: 'number', align: 'right' },
  { key: 'total_lot',           label: 'Total lot', kind: 'number', align: 'right' },
  { key: 'lot_fixed',           label: 'Lot fixed', kind: 'number', align: 'right' },
  { key: 'lot_to_fix',          label: 'Lot to fix', kind: 'number', align: 'right' },
  { key: 'avg_futures_price',   label: 'Avg. Futures Price', kind: 'number', align: 'right' },
  { key: 'kg_fixed',            label: 'Kg fixed', kind: 'number', align: 'right' },
  { key: 'kg_to_fix',           label: 'Kg to fix', kind: 'number', align: 'right' },
  { key: 'avg_price2',          label: 'Avg. Price2', kind: 'number', align: 'right' },
  { key: 'issued_text',         label: 'Issued' },
  { key: 'sent_mail_text',      label: 'Sent mail' },
  { key: 'received_mail_text',  label: 'Received mail' },
  { key: 'sent_paper_text',     label: 'Sent paper' },
  { key: 'received_paper_text', label: 'Received paper' },
  { key: 'intermediary_text',   label: 'Intermediary ?' },
  { key: 'contract_shipper_intermediary', label: 'Contract ref (Shipper w Intermediary)' },
  { key: 'shipper_code',        label: 'Shipper code' },
  { key: 'shipper_name',        label: 'Shipper Name' },
  { key: 'ptbfdiffs_shipper_intermediary', label: 'PTBF Diffs (Shipper w Intermediary)', kind: 'number', align: 'right' },
  { key: 'outright2',           label: 'Outright price (Shipper w Intermediary)', kind: 'number', align: 'right' },
  { key: 'forced_close_text',   label: 'Is Forced Close?' },
  { key: 'forced_close_date',   label: 'Forced Close Date', kind: 'date' },
  { key: 'margin_value_per',    label: 'Deposit Value (%)', kind: 'percent', align: 'right' },
  { key: 'margin_limit_per',    label: 'Deposit Limit (%)', kind: 'percent', align: 'right' },
  { key: 'margin_time_limit',   label: 'Time Limit' },
  { key: 'margin_bonus_per',    label: 'Deposit Bonus (%)', kind: 'percent', align: 'right' },
  { key: 'margin_interest_month', label: 'Deposit Interest (% per Month)', kind: 'percent', align: 'right' },
  { key: 'deposit_repayment_date', label: 'Deposit Repayment Date', kind: 'date' },
  { key: 'trader_in_charge_name', label: 'Trader In Charge' },
  { key: 'fixed_fx',            label: 'Fixed FX', kind: 'number', align: 'right' },
  { key: 'provi_market_rate',   label: 'Provisional on Market Price (%)', kind: 'percent', align: 'right' },
  { key: 'stoploss_rate',       label: 'Stoploss Rate (%)', kind: 'percent', align: 'right' },
  { key: 'date_update',         label: 'Updated Date', kind: 'datetime' },
  { key: 'person_update',       label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<string>([
  // Prices / per-unit values — sums are meaningless.
  'outright_price',
  'hedged_price',
  'avg_futures_price',
  'avg_price2',
  'outright2',
  // Diffs (price differentials).
  'ptbf_diffs',
  'implied_diffs',
  'diffs_g2_fob_bulk',
  'ptbfdiffs_shipper_intermediary',
  // FX rates.
  'fx',
  'fixed_fx',
  // Per-kg license values.
  'si',
  'sd',
])


const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Access `Format="Percent"` — display decimal as `xx.xx%` (×100, 2 decimals). */
const formatAccessPercent = (value: number): string => {
  const pct = value * 100
  const neg = pct < 0
  const abs = Math.abs(pct).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return neg ? `(${abs}%)` : `${abs}%`
}

const distinctValues = (rows: LocalPListItem[], key: keyof LocalPListItem): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const sumNumericColumn = (rows: LocalPListItem[], key: keyof LocalPListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

/**
 * Access status is based on cancel_adjust:
 *  cancel_adjust IS NOT NULL → Cancelled (Access: `cancel_adjust is null` = active)
 *  is_forced_close           → Forced-close
 *  otherwise                 → Active
 */
const statusText = (row: LocalPListItem): string => {
  if (row.cancel_adjust != null) return 'Cancelled'
  if (row.is_forced_close) return 'Forced-close'
  return 'Active'
}

/** Access `btnPrint_Click`: `is_status = 0 AND cancel_adjust IS NULL`. */
const canPrintPContractRow = (row: LocalPListItem | null): boolean =>
  !!row && (row.is_status ?? 0) === 0 && row.cancel_adjust == null

/** Access `btnSubPrint_Click` (Print - Cancel): hợp đồng đã cancel (`cancel_adjust` có giá trị). */
const canPrintCancelRow = (row: LocalPListItem | null): boolean =>
  !!row && (row.is_status ?? 0) === 0 && row.cancel_adjust != null

const cellValue = (row: LocalPListItem, col: ColumnDef): string => {
  const val = row[col.key as keyof LocalPListItem]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'percent' && typeof val === 'number') return formatAccessPercent(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

interface LocalPListPageProps {
  deliveryLatestFrom?: string
  deliveryLatestTo?: string
  isForcedClose?: 'all' | 'yes' | 'no'
}

export function LocalPListPage({ deliveryLatestFrom, deliveryLatestTo, isForcedClose }: LocalPListPageProps = {}) {
  const { canEdit } = usePermission('purchase.local_p')
  const snack = useSnackbar()

  // Hard-delete is gated by email-allowlist in tmp_add_information.value where type='contract_delete'.
  // Legacy Access rule: cleanCheck(DLookup(...)) Like "*" & USER_EMAIL & "*"
  // refetchOnMountOrArgChange ensures the check runs each time the page opens
  // (so re-login with a different account picks up the new email).
  const { data: deleteAccess } = useGetLocalPCanHardDeleteQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const canHardDelete = canEdit && (deleteAccess?.data?.can_delete ?? false)

  // ── Selection ────────────────────────────────────────────────────────────
  const {
    selectedId: selectedContractNo,
    setSelectedId: setSelectedContractNo,
    toggleSelect: toggleSelectContract,
  } = useRowSelection<string>()

  const { getWidth, startResize } = useColumnWidths('purchase.local-p')

  // ── Pagination ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1) // -1 = All

  // ── Filters ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | 'Active' | 'Cancelled' | 'Forced-close'>('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const [selectOpen, setSelectOpen] = useState(false)
  const closeFilterPopover = () => { filterPopover.close(); setSelectOpen(false) }
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => {
    filterPopover.open(kind, e)
    if (kind === 'status') setSelectOpen(true)
  }

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || !!statusFilter || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + (statusFilter ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [editingContractNo, setEditingContractNo] = useState<string | null>(null)
  const [copySourceNo, setCopySourceNo] = useState<string | null>(null)
  const [confirmCancel, setConfirmCancel] = useState<LocalPListItem | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<LocalPListItem | null>(null)
  const [printTarget, setPrintTarget] = useState<LocalPPrintDialogState | null>(null)
  const [noticeDialog, setNoticeDialog] = useState<LocalPNoticeDialogState | null>(null)

  // ── Data fetch — server-side delivery_latest and forced_close filters ───
  const forcedCloseParam =
    isForcedClose === 'yes' ? true : isForcedClose === 'no' ? false : undefined
  const { data, isLoading, isFetching, error } = useListLocalPQuery({
    page: 1,
    page_size: ALL_ROWS_PAGE_SIZE,
    is_status: undefined,
    delivery_latest_from: deliveryLatestFrom || undefined,
    delivery_latest_to: deliveryLatestTo || undefined,
    is_forced_close: forcedCloseParam,
  }, { refetchOnMountOrArgChange: true })

  const { data: editingDetail } = useGetLocalPQuery(editingContractNo ?? '', {
    skip: !editingContractNo,
  })

  const { data: copySourceDetail } = useGetLocalPQuery(copySourceNo ?? '', {
    skip: !copySourceNo,
  })

  const [cancelLocalP, { isLoading: cancelling }] = useCancelLocalPMutation()
  const [deleteLocalP, { isLoading: deleting }] = useDeleteLocalPMutation()
  const [fetchLocalPDetail, { isFetching: checkingLocalPDetail }] = useLazyGetLocalPQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'pucontract',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.local-p',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  // ── Local filter + pagination ────────────────────────────────────────────
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
        if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
        if (statusFilter && statusText(row) !== statusFilter) return false
        // Header-level filters from PurchaseContractPage
        if (deliveryLatestFrom && (!row.latest_delivery || row.latest_delivery < deliveryLatestFrom)) return false
        if (deliveryLatestTo && (!row.latest_delivery || row.latest_delivery > deliveryLatestTo)) return false
        if (isForcedClose === 'yes' && !row.is_forced_close) return false
        if (isForcedClose === 'no' && row.is_forced_close) return false
        return true
      }),
    [baseRows, searchTerm, columnFilters.filters, statusFilter, deliveryLatestFrom, deliveryLatestTo, isForcedClose],
  )

  const sortedRows = useMemo(
    () =>
      sortTableRows(filteredRows, sortState, TABLE_COLUMNS, (row, key) => {
        if (key === 'status') return statusText(row)
        return row[key as keyof LocalPListItem]
      }),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter(
            (col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
          )
          .map((col) => [
            String(col.key),
            sumNumericColumn(filteredRows, col.key as keyof LocalPListItem),
          ]),
      ),
    [filteredRows, visibleColumns],
  )

  const statusOptions = ['Active', 'Cancelled', 'Forced-close']

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => String(c.key) === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'Contract: —'
    return `Contract: ${rows[0].contract_no}–${rows[rows.length - 1].contract_no}`
  }, [rows])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<LocalPListItem | null>(
    () => baseRows.find((r) => r.contract_no === selectedContractNo) ?? null,
    [baseRows, selectedContractNo],
  )

  const printPContractEnabled = useMemo(() => canPrintPContractRow(selectedRow), [selectedRow])
  const printCancelEnabled = useMemo(() => canPrintCancelRow(selectedRow), [selectedRow])

  // ── Action handlers ───────────────────────────────────────────────────────

  const handleEdit = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (selectedRow.npe_ref) {
      snack.warning('This P Contract is fixed from NPE Contract!')
      return
    }
    if (selectedRow.cancel_adjust != null) {
      snack.warning('This P Contract is cancel! No edit!')
      return
    }
    if (selectedRow.is_forced_close) {
      snack.warning('This P Contract is closed! No edit!')
      return
    }
    setEditingContractNo(selectedRow.contract_no)
  }

  const handleCancelClick = async () => {
    if (!selectedRow) {
      setNoticeDialog({ title: 'Warning', message: 'Please choose a record!' })
      return
    }
    try {
      const detailResponse = await fetchLocalPDetail(selectedRow.contract_no, false).unwrap()
      const detail = detailResponse.data
      if (detail.npe_ref) {
        setNoticeDialog({ title: 'Warning', message: 'This P Contract is fixed from NPE Contract!' })
        return
      }
      if (detail.cancel_adjust != null) {
        setNoticeDialog({ title: 'Warning', message: 'This P Contract is cancel!' })
        return
      }
      setConfirmCancel({
        ...selectedRow,
        cancel_adjust: detail.cancel_adjust,
        is_status: detail.is_status ?? selectedRow.is_status,
        npe_ref: detail.npe_ref,
      })
    } catch (err) {
      setNoticeDialog({ title: 'Warning', message: getErrorMessage(err, 'Failed to load P Contract') })
    }
  }

  const handleConfirmCancel = async () => {
    if (!confirmCancel) return
    try {
      await cancelLocalP(confirmCancel.contract_no).unwrap()
      snack.success(`Successfully Cancelled`)
      setConfirmCancel(null)
      if (selectedContractNo === confirmCancel.contract_no) setSelectedContractNo(null)
    } catch (err) {
      const message = getErrorMessage(err, 'Cancel contract failed')
      if (typeof err === 'object' && err && 'status' in err) {
        const status = (err as { status?: number }).status
        if (status === 403) {
          setNoticeDialog({ title: 'Permission denied', message })
          return
        }
        if (status === 400 && message === 'This P Contract is cancel!') {
          setNoticeDialog({
            title: 'Warning',
            message: 'This P Contract has already been cancelled!',
          })
          setConfirmCancel(null)
          return
        }
        if (status === 400) {
          setNoticeDialog({ title: 'Warning', message })
          setConfirmCancel(null)
          return
        }
      }
      if (message === 'This P Contract is cancel!') {
        setNoticeDialog({
          title: 'Warning',
          message: 'This P Contract has already been cancelled!',
        })
        setConfirmCancel(null)
        return
      }
      snack.error(message)
    }
  }

  const handleCopy = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setCopySourceNo(selectedRow.contract_no)
  }

  const handlePrintCancel = async () => {
    if (!selectedRow) {
      setNoticeDialog({ title: 'Warning', message: 'Please choose a record!' })
      return
    }
    try {
      const detailResponse = await fetchLocalPDetail(selectedRow.contract_no, false).unwrap()
      const detail = detailResponse.data
      if ((detail.is_status ?? 0) === 0 && detail.cancel_adjust == null) {
        setNoticeDialog({ title: 'Warning', message: 'This P contract is not cancel!' })
        return
      }
      setPrintTarget({ row: { ...selectedRow, cancel_adjust: detail.cancel_adjust, is_status: detail.is_status ?? selectedRow.is_status }, kind: 'cancel' })
    } catch (err) {
      setNoticeDialog({ title: 'Warning', message: getErrorMessage(err, 'Print Cancel failed') })
    }
  }

  const handlePrintContract = async () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    try {
      const detailResponse = await fetchLocalPDetail(selectedRow.contract_no, false).unwrap()
      const detail = detailResponse.data
      if ((detail.is_status ?? 0) !== 0 || detail.cancel_adjust != null) {
        setNoticeDialog({ title: 'Warning', message: 'This P contract is cancel!' })
        return
      }
      setPrintTarget({
        row: {
          ...selectedRow,
          cancel_adjust: detail.cancel_adjust,
          is_status: detail.is_status ?? selectedRow.is_status,
          npe_ref: detail.npe_ref,
        },
        kind: 'contract',
      })
    } catch (err) {
      setNoticeDialog({ title: 'Warning', message: getErrorMessage(err, 'Print P Contract failed') })
      return
    }
  }

  const handleDeleteClick = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (selectedRow.npe_ref) {
      snack.error('This P Contract is fixed from NPE Contract!')
      return
    }
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteLocalP(confirmDelete.contract_no).unwrap()
      snack.success(`Contract ${confirmDelete.contract_no} deleted`)
      setConfirmDelete(null)
      if (selectedContractNo === confirmDelete.contract_no) setSelectedContractNo(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete contract failed'))
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button
            variant="contained"
            size="small"
            color="success"
            sx={actionToolbarBtnSx}
            disabled={!canEdit}
            onClick={() => setCreateOpen(true)}
          >
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit || checkingLocalPDetail}
            onClick={() => void handleCancelClick()}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={handleCopy}
          >
            Copy
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || checkingLocalPDetail || !printCancelEnabled}
            onClick={() => void handlePrintCancel()}
          >
            {checkingLocalPDetail ? <CircularProgress size={11} color="inherit" sx={{ mr: 0.5 }} /> : null}
            Print - Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || checkingLocalPDetail || !printPContractEnabled}
            onClick={() => void handlePrintContract()}
          >
            {checkingLocalPDetail ? <CircularProgress size={11} color="inherit" sx={{ mr: 0.5 }} /> : null}
            Print - P Contract
          </Button>
          {canHardDelete && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
        </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => openFilterPopover('search', e)}
        />
        <FilterButton
          label="Status"
          active={!!statusFilter}
          onClick={(e) => openFilterPopover('status', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openMenu(e.currentTarget)}
          sx={filterBarBtnSx}
        >
          Columns
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        {/* Pagination toolbar */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        {/* Filter popovers — Search global + Status (cross-column computed) */}
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, vendor, ref..."
              label="Search P Contracts"
              onClear={closeFilterPopover}
            />
          )}
          {filterPopover.isOpen('status') && (
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => { setSelectOpen(false); closeFilterPopover() }}
                onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(0) }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {statusOptions.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        {/* Virtualized table */}
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load P Contracts"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No P Contracts found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(String(col.key))}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, String(col.key))}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: String(col.key), position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatAccessNumber(total)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPListItem) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(String(col.key))
                return (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: MuiTableProps) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 2000, width: '100%' }} />,
              TableHead: (props: MuiTableHeadProps) => <TableHead {...props} component="thead" />,
              TableBody: (props: MuiTableBodyProps) => <TableBody {...props} component="tbody" />,
              TableRow: (props: VirtuosoTableRowProps) => {
                const idx = Number(props['data-item-index'])
                const rowContractNo = Number.isFinite(idx) ? rows[idx]?.contract_no : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowContractNo != null && rowContractNo === selectedContractNo}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowContractNo != null) toggleSelectContract(rowContractNo)
                    }}
                  />
                )
              },
            }}
          />
          )}
        </Box>
      </Box>

      {/* Dialogs */}
      <LocalPFormDialog
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
      />
      <LocalPFormDialog
        open={!!editingContractNo && !!editingDetail?.data}
        mode="edit"
        initialValue={editingDetail?.data ?? null}
        onClose={() => setEditingContractNo(null)}
      />
      <LocalPFormDialog
        open={!!copySourceNo && !!copySourceDetail?.data}
        mode="copy"
        copySource={copySourceDetail?.data ?? null}
        onClose={() => setCopySourceNo(null)}
        onSaved={() => { setCopySourceNo(null) }}
      />
      <ConfirmDialog
        open={!!confirmCancel}
        title="Cancel Contract"
        message={confirmCancel ? `Do you want to Cancel Contract '${confirmCancel.contract_no}'?` : ''}
        destructive
        loading={cancelling}
        onConfirm={() => void handleConfirmCancel()}
        onCancel={() => setConfirmCancel(null)}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Contract"
        message={confirmDelete ? `Do you want to delete Contract P = '${confirmDelete.contract_no}' ?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <LocalPPrintDialog
        open={Boolean(printTarget)}
        printTarget={printTarget}
        onClose={() => setPrintTarget(null)}
      />
      <LocalPNoticeDialog
        open={Boolean(noticeDialog)}
        notice={noticeDialog}
        onClose={() => setNoticeDialog(null)}
      />
    </Box>
  )
}

export default LocalPListPage
